import {
  AutoAwesome as AIIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { hostname, parseError } from "../../utils";
import LoadingButton from "../../components/LoadingButton";

interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "warning" | "error" | "success" | "feature";
  priority: "low" | "normal" | "high";
  isSystemWide: boolean;
  requiresAcknowledgment: boolean;
  publishAt: string | null;
  expiresAt: string | null;
  notificationData: any;
  createdByUid: string | null;
  uid: string | null;
  companyId: string | null;
  createdDate: string;
  updatedDate: string | null;
}

interface NotificationManagementProps {
  getToken: () => Promise<string>;
}

const NotificationManagement: React.FC<NotificationManagementProps> = ({
  getToken,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [scopeFilter, setScopeFilter] = useState<string>("all");

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info" as const,
    priority: "normal" as const,
    isSystemWide: true,
    requiresAcknowledgment: false,
    publishAt: "",
    expiresAt: "",
    uid: "",
    companyId: "",
    context: "platform update",
  });

  const [enhancedMessage, setEnhancedMessage] = useState("");
  const [showEnhanced, setShowEnhanced] = useState(false);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const offset = page * rowsPerPage;
      const params: any = {
        limit: rowsPerPage,
        offset: offset,
      };

      if (typeFilter && typeFilter !== "all") {
        params.type = typeFilter;
      }

      if (scopeFilter && scopeFilter !== "all") {
        params.systemWide = scopeFilter === "system-wide" ? "true" : "false";
      }

      const response = await axios.get(`${hostname}/notifications/admin`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotifications(response.data.notifications);
      setTotalCount(
        response.data.pagination?.total || response.data.notifications.length
      );
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  // Create notification
  const createNotification = async () => {
    if (!formData.title || !formData.message) return;

    setCreating(true);
    setError(null);

    try {
      const token = await getToken();
      const payload = {
        title: formData.title,
        message: showEnhanced ? enhancedMessage : formData.message,
        type: formData.type,
        priority: formData.priority,
        isSystemWide: formData.isSystemWide,
        requiresAcknowledgment: formData.requiresAcknowledgment,
        publishAt: formData.publishAt
          ? moment(formData.publishAt).toISOString()
          : null,
        expiresAt: formData.expiresAt
          ? moment(formData.expiresAt).toISOString()
          : null,
        uid: formData.isSystemWide ? null : formData.uid || null,
        companyId: formData.isSystemWide ? null : formData.companyId || null,
      };

      await axios.post(`${hostname}/notifications/admin`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Reset form
      setFormData({
        title: "",
        message: "",
        type: "info",
        priority: "normal",
        isSystemWide: true,
        requiresAcknowledgment: false,
        publishAt: "",
        expiresAt: "",
        uid: "",
        companyId: "",
        context: "platform update",
      });
      setEnhancedMessage("");
      setShowEnhanced(false);
      setSuccess("Notification created successfully");

      // Refresh list and reset to first page
      setPage(0);
      await fetchNotifications();

      // Switch to list tab
      setActiveTab(1);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setCreating(false);
    }
  };

  // Enhance message with AI
  const enhanceMessage = async () => {
    if (!formData.message) return;

    setEnhancing(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/notifications/admin/ai-enhance`,
        {
          message: formData.message,
          context: formData.context,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setEnhancedMessage(response.data.enhancedMessage);
      setShowEnhanced(true);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setEnhancing(false);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    setDeleting(notificationId);

    try {
      const token = await getToken();
      await axios.delete(`${hostname}/notifications/admin/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Remove from list
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setSuccess("Notification deleted successfully");
    } catch (err) {
      setError(parseError(err));
    } finally {
      setDeleting(null);
    }
  };

  // Format date using moment
  const formatDate = (dateString: string) => {
    return moment(dateString).format("MMM D, YYYY h:mm A");
  };

  // Get type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case "warning":
        return "warning";
      case "error":
        return "error";
      case "success":
        return "primary";
      case "feature":
        return "secondary";
      default:
        return "primary";
    }
  };

  // Load notifications on mount and when pagination or filter changes
  useEffect(() => {
    fetchNotifications();
  }, [page, rowsPerPage, typeFilter, scopeFilter]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "message") {
      setShowEnhanced(false);
      setEnhancedMessage("");
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setPage(0);
  };

  const handleScopeFilterChange = (value: string) => {
    setScopeFilter(value);
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Notification Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Create Notification" />
        <Tab label="Manage Notifications" />
      </Tabs>

      {/* Create Notification Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={(e) => handleInputChange("type", e.target.value)}
                  >
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="feature">Feature</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) =>
                      handleInputChange("priority", e.target.value)
                    }
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Message */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  required
                  helperText="Enter your message. You can use AI to enhance it for professional communication."
                />
              </Grid>

              {/* AI Enhancement */}
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
                >
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Context</InputLabel>
                    <Select
                      value={formData.context}
                      label="Context"
                      onChange={(e) =>
                        handleInputChange("context", e.target.value)
                      }
                      size="small"
                    >
                      <MenuItem value="platform update">
                        Platform Update
                      </MenuItem>
                      <MenuItem value="new feature">New Feature</MenuItem>
                      <MenuItem value="maintenance">Maintenance</MenuItem>
                      <MenuItem value="issue resolution">
                        Issue Resolution
                      </MenuItem>
                      <MenuItem value="announcement">Announcement</MenuItem>
                    </Select>
                  </FormControl>

                  <LoadingButton
                    variant="outlined"
                    startIcon={<AIIcon />}
                    onClick={enhanceMessage}
                    loading={enhancing}
                    disabled={!formData.message || enhancing}
                  >
                    Enhance with AI
                  </LoadingButton>

                  {showEnhanced && (
                    <Button
                      variant="text"
                      onClick={() => setShowEnhanced(false)}
                    >
                      Use Original
                    </Button>
                  )}
                </Box>

                {showEnhanced && enhancedMessage && (
                  <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setShowEnhanced(true)}
                      >
                        Use Enhanced
                      </Button>
                    }
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      AI Enhanced Message:
                    </Typography>
                    <Typography variant="body2">{enhancedMessage}</Typography>
                  </Alert>
                )}
              </Grid>

              {/* Targeting */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Targeting
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isSystemWide}
                      onChange={(e) =>
                        handleInputChange("isSystemWide", e.target.checked)
                      }
                    />
                  }
                  label="System-wide notification (all users)"
                />
              </Grid>

              {!formData.isSystemWide && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Target User ID (optional)"
                      value={formData.uid}
                      onChange={(e) => handleInputChange("uid", e.target.value)}
                      helperText="Leave empty for all users in company"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Company ID (optional)"
                      value={formData.companyId}
                      onChange={(e) =>
                        handleInputChange("companyId", e.target.value)
                      }
                      helperText="Leave empty for all companies"
                    />
                  </Grid>
                </>
              )}

              {/* Options */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Options
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.requiresAcknowledgment}
                      onChange={(e) =>
                        handleInputChange(
                          "requiresAcknowledgment",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Requires acknowledgment"
                />
              </Grid>

              {/* Scheduling */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Scheduling (Optional)
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Publish At"
                  type="datetime-local"
                  value={formData.publishAt}
                  onChange={(e) =>
                    handleInputChange("publishAt", e.target.value)
                  }
                  helperText="Leave empty to publish immediately"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Expires At"
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) =>
                    handleInputChange("expiresAt", e.target.value)
                  }
                  helperText="Leave empty for no expiration"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <LoadingButton
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={createNotification}
                    loading={creating}
                    disabled={!formData.title || !formData.message || creating}
                  >
                    Create Notification
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Manage Notifications Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Filter Controls */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={typeFilter}
                label="Filter by Type"
                onChange={(e) => handleTypeFilterChange(e.target.value)}
                size="small"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="feature">Feature</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Scope</InputLabel>
              <Select
                value={scopeFilter}
                label="Filter by Scope"
                onChange={(e) => handleScopeFilterChange(e.target.value)}
                size="small"
              >
                <MenuItem value="all">All Scopes</MenuItem>
                <MenuItem value="system-wide">System-wide</MenuItem>
                <MenuItem value="targeted">Targeted</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.message.substring(0, 50)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={notification.type}
                          size="small"
                          color={getTypeColor(notification.type) as any}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={notification.priority}
                          size="small"
                          color={
                            notification.priority === "high"
                              ? "error"
                              : "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            notification.isSystemWide
                              ? "System-wide"
                              : "Targeted"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(notification.createdDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() =>
                              deleteNotification(notification.id)
                            }
                            disabled={deleting === notification.id}
                            color="error"
                          >
                            {deleting === notification.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {notifications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No notifications found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default NotificationManagement;
